// Step 2 of the import wizard: match the file's columns to our fields (or keep
// them as extra fields, or leave them out), and answer the questions the file
// raised. Holds the "Goes to" list, a combobox that can be searched, and the
// table the columns are matched in (release 3.3, I2; the rules are plain
// functions in columnTiers.ts).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { ImportError } from '../../components/ImportUndo'
import { Chip } from '../../components/StatusChip'
import { Icon } from '../../components/ui'
import type { ImportAnalysis, ImportColumnChoices } from '../../lib/api'
import { COLUMN_CHECK, COLUMN_MATCHED, COLUMN_NONE, KEEP_EXTRA, plural } from '../../lib/importWords'
import { NEEDS_ANSWER, openQuestions } from './shared'
import {
  acceptable,
  chipWord,
  columnRows,
  cutValue,
  defaultMappingName,
  listWords,
  MAPPING_NAME_MAX,
  mappingName,
  missingRequired,
  NOT_OURS,
  pickerOptions,
  searchOptions,
  tierCounts,
  unmatchedWords,
  whyNotNext,
  type ColumnRow,
  type ColumnTier,
  type PickerOption,
} from './columnTiers'
import { DRAFT_SAVED, type Remember } from './draftSync'
import '../../styles/home.css'
import './columns.css'

export { pickerOptions } from './columnTiers'

// ------------------------------------------------- the "Goes to" combobox

// A button that shows the choice; pressed, a search box over the grouped list
// opens under it (or over it, near the bottom of the window). Arrow keys move,
// Home and End jump, Enter picks, Escape and Tab close and return to the
// button. The list is drawn on the page's body, so the table's scroll panel
// never cuts it off. Searching never hides "Keep as extra field" and "Do not
// import": they end every list.
const POP_MIN = 160
const POP_MAX = 360
const POP_WIDTH = 280

type PopPos = { top?: number; bottom?: number; left: number; width: number; height: number }

function FieldPicker({
  id,
  label,
  value,
  options,
  noteFor,
  onChange,
  disabled = false,
}: {
  id: string
  label: string
  value: string
  options: PickerOption[]
  noteFor: (value: string) => string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const [pos, setPos] = useState<PopPos | null>(null)
  // Whether the arrow keys chose the option: after a search that matched no
  // field, Enter must not quietly pick "Keep as extra field".
  const moved = useRef(false)
  const button = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)

  const shown = useMemo(() => searchOptions(options, q), [options, q])
  const fieldsShown = shown.filter((o) => o.group !== NOT_OURS).length
  const current = options.find((o) => o.value === value)

  const place = useCallback(() => {
    const b = button.current
    if (!b) return
    const r = b.getBoundingClientRect()
    const width = Math.max(r.width, POP_WIDTH)
    const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8))
    const below = window.innerHeight - r.bottom - 12
    const above = r.top - 12
    if (below >= POP_MAX || below >= above) setPos({ top: r.bottom + 4, left, width, height: Math.max(POP_MIN, Math.min(POP_MAX, below)) })
    else setPos({ bottom: window.innerHeight - r.top + 4, left, width, height: Math.max(POP_MIN, Math.min(POP_MAX, above)) })
  }, [])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  // The search box takes the focus once the list is drawn (it is placed
  // first, so it never flashes at the wrong spot).
  const shownNow = open && pos !== null
  useEffect(() => {
    if (shownNow) search.current?.focus()
  }, [shownNow])

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      const t = e.target as Node
      if (button.current?.contains(t) || pop.current?.contains(t)) return
      setOpen(false)
      setPos(null)
    }
    // The page or the table's panel scrolled: the list follows its button.
    // Scrolling the list itself moves nothing.
    const scrolled = (e: Event) => {
      if (pop.current?.contains(e.target as Node)) return
      place()
    }
    document.addEventListener('mousedown', away)
    window.addEventListener('scroll', scrolled, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('mousedown', away)
      window.removeEventListener('scroll', scrolled, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  // The option the arrow keys reached stays in view.
  useEffect(() => {
    if (open) document.getElementById(id + '-o' + active)?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active, id])

  function openList() {
    moved.current = false
    setQ('')
    setActive(Math.max(0, searchOptions(options, '').findIndex((o) => o.value === value)))
    setOpen(true)
  }

  function close() {
    setOpen(false)
    setPos(null)
    button.current?.focus()
  }

  function pick(o: PickerOption) {
    onChange(o.value)
    close()
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moved.current = true
      setActive((i) => Math.min(i + 1, shown.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      moved.current = true
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home') {
      e.preventDefault()
      moved.current = true
      setActive(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      moved.current = true
      setActive(shown.length - 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const o = shown[active]
      if (q.trim() && fieldsShown === 0 && !moved.current) return
      if (o) pick(o)
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      e.preventDefault()
      e.stopPropagation()
      close()
    }
  }

  // The list keeps the groups in order; the flat index is what the arrow keys
  // move along.
  const groups: { name: string; items: { o: PickerOption; i: number }[] }[] = []
  shown.forEach((o, i) => {
    const last = groups[groups.length - 1]
    if (last && last.name === o.group) last.items.push({ o, i })
    else groups.push({ name: o.group, items: [{ o, i }] })
  })

  const list = open && pos && (
    <div
      ref={pop}
      className="imw-pop imw-pop-fixed"
      style={{ top: pos.top ?? 'auto', bottom: pos.bottom ?? 'auto', left: pos.left, width: pos.width, maxHeight: pos.height }}
    >
      <input
        ref={search}
        type="search"
        role="combobox"
        aria-expanded="true"
        aria-controls={id + '-list'}
        aria-autocomplete="list"
        aria-activedescendant={shown[active] ? id + '-o' + active : undefined}
        aria-label={'Search our fields: ' + label}
        placeholder="Search our fields"
        autoComplete="off"
        value={q}
        onChange={(e) => {
          setQ(e.target.value)
          setActive(0)
        }}
        onKeyDown={onKey}
      />
      {fieldsShown === 0 && <p className="imw-none">No field matches. Try another word.</p>}
      <div className="imw-opts" role="listbox" id={id + '-list'} aria-label={label}>
        {groups.map((g, gi) => (
          <div key={g.name + gi} role="group" aria-labelledby={id + '-g' + gi}>
            <div className="imw-grp" id={id + '-g' + gi}>
              {g.name}
            </div>
            {g.items.map(({ o, i }) => {
              const note = noteFor(o.value)
              return (
                <div
                  key={i}
                  id={id + '-o' + i}
                  role="option"
                  aria-selected={o.value === value}
                  className={'imw-opt' + (i === active ? ' on' : '')}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(o)
                  }}
                  onMouseEnter={() => setActive(i)}
                >
                  {o.label}
                  {note && <span className="imw-taken">{note}</span>}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="imw-picker">
      <button
        ref={button}
        id={id}
        type="button"
        className="imw-picker-btn"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label + ': ' + (current?.label ?? 'not chosen')}
        onClick={() => (open ? close() : openList())}
        onKeyDown={(e) => {
          if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !open) {
            e.preventDefault()
            openList()
          }
        }}
      >
        <span className="imw-picker-val">{current?.label ?? 'Choose'}</span>
        <Icon name="chevron" size={14} />
      </button>
      {list && typeof document !== 'undefined' ? createPortal(list, document.body) : null}
    </div>
  )
}

// ------------------------------------------------- 2. the columns
//
// Release 3.3 (I2): a table, not a form (plan 4.3). Your column, its first
// values, where it goes, and how sure the match is. Matched rows fold under one
// line; Review and Unmatched rows stay open. The questions the file raised come
// first, as one card, because Next waits for them. At phone width the table is
// a stack of cards, the file's column first in each.

const TIER_WORD: Record<ColumnTier, typeof COLUMN_MATCHED> = {
  MATCHED: COLUMN_MATCHED,
  REVIEW: COLUMN_CHECK,
  UNMATCHED: COLUMN_NONE,
}

export function ColumnStep({
  analysis,
  choices,
  confirmed,
  byPerson = new Set<string>(),
  answers,
  remember: rememberGiven,
  busy,
  error,
  draftNote = '',
  showAllFirst = false,
  readOnly = false,
  onChoose,
  onConfirm,
  onAnswer,
  onRemember = () => {},
  onReset,
  onBack,
  onNext,
}: {
  analysis: ImportAnalysis
  choices: ImportColumnChoices
  confirmed: Record<string, boolean>
  // The columns a person chose by hand: "Matched (you)".
  byPerson?: ReadonlySet<string>
  answers: Record<string, string>
  remember?: Remember
  busy: boolean
  error: unknown
  // The footer's quiet line about the server draft.
  draftNote?: string
  // Opened from Upload's "Change": every row shown, not only those to review.
  showAllFirst?: boolean
  // Somebody else's import, or one that can no longer change: shown, not editable.
  readOnly?: boolean
  onChoose: (column: string, value: string) => void
  onConfirm: (columns: string[]) => void
  onAnswer: (key: string, code: string) => void
  onRemember?: (r: Remember) => void
  onReset: () => void
  onBack: () => void
  onNext: () => void
}) {
  const remember = rememberGiven ?? { on: !analysis.savedMapping, name: defaultMappingName(analysis) }
  // The file arrived on a saved mapping and the name is still that mapping's:
  // ticking the box overwrites it, so it says so (and starts unticked, in the
  // shell, so a one-off change never rewrites a shared mapping by default).
  const updatesSaved = Boolean(analysis.savedMapping && mappingName(remember.name) === analysis.savedMapping.name)
  const fields = analysis.fields
  const byKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields])
  const options = useMemo(() => pickerOptions(fields), [fields])
  const rows = columnRows(analysis, choices, confirmed, byPerson)
  const counts = tierCounts(rows)
  // Rows changed since the filter was last chosen stay where they are, so a row
  // just matched does not vanish (and take the focus with it) under the person.
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set())
  const touch = (cols: string[]) => setTouched((t) => new Set([...t, ...cols]))
  const summary = useRef<HTMLParagraphElement>(null)
  const open = rows.filter((r) => r.tier !== 'MATCHED')
  const kept = rows.filter((r) => r.tier !== 'MATCHED' || touched.has(r.col.column))
  const matchedRows = rows.filter((r) => r.tier === 'MATCHED' && !touched.has(r.col.column))
  const toAccept = acceptable(rows)
  const missing = missingRequired(analysis, rows)
  const asked = (analysis.questions ?? []).filter((q) => q.asked)
  const unanswered = openQuestions(analysis, answers)
  const whyNot = whyNotNext(analysis, rows, unanswered.length)
  // All rows, or only what needs somebody; a choice made here stays until the
  // person changes it.
  const [showAll, setShowAll] = useState(showAllFirst)
  const [foldOpen, setFoldOpen] = useState(false)
  const shown = showAll ? rows : kept

  const usedBy = new Map<string, string[]>()
  for (const r of rows) if (r.field) usedBy.set(r.choice, [...(usedBy.get(r.choice) ?? []), r.col.column])

  const row = (r: ColumnRow) => (
    <tr key={r.col.index} className={'imw-crow imw-crow-' + r.tier.toLowerCase()} data-column={r.col.column}>
      <th scope="row" className="imw-cname">
        <span title={r.name}>{r.name}</span>
      </th>
      <td className="imw-cvals" data-label="First values">
        {r.samples.length === 0 ? (
          <span className="imw-muted">Empty in every row</span>
        ) : (
          r.samples.map((v, i) => (
            <span key={i} className="imw-cval" title={v}>
              {cutValue(v)}
            </span>
          ))
        )}
      </td>
      <td className="imw-cgoes" data-label="Goes to">
        <FieldPicker
          id={'imw-col-' + r.col.index}
          label={'Goes to, for ' + r.name}
          value={r.choice}
          options={options}
          noteFor={(v) => {
            const f = byKey.get(v)
            if (!f || f.many) return ''
            const others = (usedBy.get(v) ?? []).filter((x) => x !== r.col.column)
            return others.length > 0 ? 'used for ' + others[0] : ''
          }}
          onChange={(v) => {
            touch([r.col.column])
            onChoose(r.col.column, v)
          }}
          disabled={readOnly || busy}
        />
        {r.clash && <span className="imw-small imw-warn">{r.field?.label} is also chosen for another column. Keep it on one.</span>}
        {r.choice === KEEP_EXTRA && r.col.personal && <span className="imw-small">Kept as personal data.</span>}
      </td>
      <td className="imw-cmatch" data-label="Match">
        <span className="imw-status">
          <Chip s={TIER_WORD[r.tier]}>{chipWord(r)}</Chip>
          {r.tier === 'REVIEW' && r.field && (
            <button
              type="button"
              className="linkish imw-confirm"
              disabled={readOnly || busy}
              onClick={() => {
                touch([r.col.column])
                onConfirm([r.col.column])
              }}
            >
              Confirm<span className="vh"> {r.name}</span>
            </button>
          )}
        </span>
        {r.why && <span className="imw-small">{r.why}</span>}
      </td>
    </tr>
  )

  return (
    <section className="panel imw-colstep" aria-labelledby="imw-cols-h">
      <div className="imw-colhead">
        <h2 id="imw-cols-h">Match columns</h2>
        <p className="imw-colsum" aria-live="polite" ref={summary} tabIndex={-1}>
          <span>
            <b>{counts.MATCHED}</b> matched
          </span>
          <span>
            <b>{counts.REVIEW}</b> to review
          </span>
          <span>{unmatchedWords(rows)}</span>
          <span className={missing.length > 0 ? 'imw-need-miss' : 'imw-need-ok'}>
            <Icon name={missing.length > 0 ? 'alert' : 'check'} size={14} />
            {missing.length > 0 ? 'Still needed: ' + listWords(missing.map((f) => f.label)) : 'Required fields: all matched'}
          </span>
        </p>
      </div>

      {asked.length > 0 && (
        <div className="imw-qcard" role="group" aria-label="Questions about your file">
          {asked.map((q, qi) => (
            <fieldset key={qi}>
              <legend>{q.text}</legend>
              {q.why && <span className="imw-small">{q.why}</span>}
              {unanswered.includes(q) && (
                <span className="imw-small">
                  <Chip s={NEEDS_ANSWER} />
                </span>
              )}
              <div className="imw-radios">
                {(q.options ?? []).map((o, oi) => (
                  <label key={oi}>
                    <input
                      type="radio"
                      name={'imw-q-' + qi}
                      value={String(oi)}
                      checked={(answers[q.key] ?? q.answer) === o.code}
                      onChange={() => onAnswer(q.key, o.code)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
      )}

      <div className="imw-colbar">
        <span className="hm-seg" role="group" aria-label="Show columns">
          <button
            type="button"
            aria-pressed={!showAll}
            onClick={() => {
              setTouched(new Set())
              setShowAll(false)
            }}
          >
            To review and unmatched ({open.length})
          </button>
          <button
            type="button"
            aria-pressed={showAll}
            onClick={() => {
              setTouched(new Set())
              setShowAll(true)
            }}
          >
            All {rows.length}
          </button>
        </span>
        {toAccept.length > 0 && (
          <button
            type="button"
            disabled={readOnly || busy}
            onClick={() => {
              const cols = toAccept.map((r) => r.col.column)
              touch(cols)
              onConfirm(cols)
              // The button goes with the last suggestion: the summary takes the focus.
              summary.current?.focus()
            }}
          >
            Accept {plural(toAccept.length, 'suggestion')}
          </button>
        )}
      </div>

      <div className="imw-coltable-wrap" role="region" aria-label="Columns of your file" tabIndex={0}>
        <table className="imw-coltable imw-stack">
          <thead>
            <tr>
              <th scope="col">Your column</th>
              <th scope="col">First values</th>
              <th scope="col">Goes to</th>
              <th scope="col">Match</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr className="imw-crow-none">
                <td colSpan={4} className="imw-muted">
                  Nothing to review: every column is matched.
                </td>
              </tr>
            )}
            {shown.map(row)}
          </tbody>
          {!showAll && matchedRows.length > 0 && (
            <tbody className="imw-fold">
              <tr className="imw-foldrow">
                <td colSpan={4}>
                  <button type="button" className="imw-foldbtn" aria-expanded={foldOpen} onClick={() => setFoldOpen((v) => !v)}>
                    <Icon name="chevron" size={14} />
                    <span>
                      {matchedRows.length} matched {matchedRows.length === 1 ? 'column' : 'columns'}
                    </span>
                    <span className="imw-muted">
                      {matchedRows
                        .slice(0, 6)
                        .map((r) => r.name)
                        .join(', ')}
                      {matchedRows.length > 6 ? ', ...' : ''}
                    </span>
                  </button>
                </td>
              </tr>
              {foldOpen && matchedRows.map(row)}
            </tbody>
          )}
        </table>
      </div>

      <ImportError error={error} />
      <div className="imw-step-foot imw-colfoot">
        <button type="button" onClick={onBack} disabled={busy}>
          Back
        </button>
        <span className="imw-remember">
          <label>
            <input
              type="checkbox"
              checked={remember.on}
              disabled={readOnly}
              onChange={(e) => onRemember({ ...remember, on: e.target.checked })}
            />
            {updatesSaved ? 'Update the saved mapping' : 'Remember this mapping as'}
          </label>
          <input
            type="text"
            aria-label="Name of the remembered mapping"
            value={remember.name}
            disabled={!remember.on || readOnly}
            maxLength={MAPPING_NAME_MAX}
            onChange={(e) => onRemember({ ...remember, name: e.target.value })}
          />
        </span>
        <button type="button" onClick={onReset} disabled={busy || readOnly}>
          Reset
        </button>
        {draftNote && (
          <span className={'imw-draft' + (draftNote === DRAFT_SAVED ? '' : ' imw-draft-said')} role={draftNote === DRAFT_SAVED ? undefined : 'status'}>
            {draftNote}
          </span>
        )}
        <button
          type="button"
          className="primary imw-push"
          disabled={busy || readOnly || whyNot !== ''}
          onClick={onNext}
          aria-describedby={whyNot ? 'imw-cols-why' : undefined}
        >
          {busy ? 'Reading...' : 'Next'}
        </button>
        {whyNot && (
          <p className="imw-why-not" id="imw-cols-why">
            {whyNot}
          </p>
        )}
      </div>
    </section>
  )
}
